import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdDelete,
  MdEditSquare,
  MdExpandLess,
  MdExpandMore,
  MdLink,
} from "react-icons/md";
import CarrierLogo from "../CarrierLogo";
import CarrierToggle from "../CarrierToggle";
import ExpandAllButton from "../ExpandAllButton";
import { EmptyState, LoadingCards } from "../ListStates";
import SearchToolbar from "../SearchToolbar";
import SectionHeader from "../SectionHeader";
import ServicesPill from "../ServicesPill";
import ViewSwitch from "../ViewSwitch";
import CarrierCard from "../Network/CarrierCard";
import ServiceChecklist from "../Network/ServiceChecklist";
import EditAccountDialog from "./EditAccountDialog";
import DisconnectDialog from "./DisconnectDialog";
import ConnectAccountDialog from "./ConnectAccountDialog";
import {
  carrierLogo,
  carrierName,
  isCarrierOn,
  serviceEntries,
  servicesOnCount,
} from "../../carrierHelpers";

const HEAD_CELL =
  "whitespace-nowrap border-b border-ui-border px-3.5 py-2.5 text-[11px] font-medium text-ui-text-muted";
const ACTION_BUTTON =
  "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full border border-ui-border bg-ui-surface px-2.5 py-1 text-xs font-medium text-ui-text-muted transition-colors";
const ACTION_HOVER = "hover:border-brand hover:bg-brand-light hover:text-brand";
const DANGER_HOVER =
  "hover:border-state-danger-border-strong hover:bg-state-danger-bg hover:text-state-danger-solid";

const ConnectButton = ({ onClick, className = "" }) => {
  const { t } = useTranslation("settings");
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-lg bg-brand px-3.5 text-[13px] font-medium text-brand-contrast hover:bg-brand-hover ${className}`}
    >
      <MdAdd className="h-[18px] w-[18px]" />
      {t("carriers.connectAccount")}
    </button>
  );
};

const AccountActions = ({ onEdit, onDelete, buttonClassName = "" }) => {
  const { t } = useTranslation("settings");
  return (
    <>
      <button
        type="button"
        onClick={onEdit}
        className={`${ACTION_BUTTON} ${ACTION_HOVER} ${buttonClassName}`}
      >
        <MdEditSquare className="h-3.5 w-3.5" />
        {t("common:actions.edit")}
      </button>
      <button
        type="button"
        onClick={onDelete}
        className={`${ACTION_BUTTON} ${DANGER_HOVER} ${buttonClassName}`}
      >
        <MdDelete className="h-3.5 w-3.5" />
        {t("common:actions.delete")}
      </button>
    </>
  );
};

const AccountsTab = ({
  rows,
  loading,
  busy,
  setEnabled,
  setServices,
  rename,
  remove,
  reload,
  upcoming,
}) => {
  const { t } = useTranslation("settings");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("cards");
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);

  const query = search.trim().toLowerCase();
  const visible = rows.filter(
    (row) =>
      (row.account_name ?? "").toLowerCase().includes(query) ||
      carrierName(row).toLowerCase().includes(query),
  );
  const allExpanded = visible.length > 0 && visible.every((row) => expanded[row.id]);

  const toggleExpanded = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleAllExpanded = () =>
    setExpanded(allExpanded ? {} : Object.fromEntries(visible.map((row) => [row.id, true])));
  const changeServices = (row, changes) => setServices([row], () => changes);

  const actionsFor = (row) => ({
    onEdit: () => setEditing(row),
    onDelete: () => setDisconnecting(row),
  });
  const toggleLabel = (row) =>
    t("carriers.useInQuotes", { name: row.account_name ?? carrierName(row) });

  const renderBody = () => {
    if (loading) return <LoadingCards account count={4} />;
    if (!rows.length) {
      return (
        <div className="flex flex-col items-center gap-2.5 rounded-xl border border-dashed border-ui-border-strong bg-ui-surface px-5 py-12 text-center">
          <MdLink className="h-10 w-10 text-brand" />
          <div className="text-base font-semibold text-ui-text">{t("carriers.noAccounts")}</div>
          <div className="max-w-[420px] text-[13px] text-ui-text-muted">
            {t("carriers.bringAccount")}
          </div>
          <ConnectButton onClick={() => setConnectOpen(true)} className="mt-1.5 h-9" />
        </div>
      );
    }
    if (!visible.length) return <EmptyState>{t("carriers.noAccountsMatch")}</EmptyState>;

    return (
      <>
        <div className="divide-y divide-ui-border-subtle overflow-hidden rounded-xl border border-ui-border bg-ui-surface sm:hidden">
          {visible.map((row) => {
            const name = carrierName(row);
            const on = isCarrierOn(row);
            const total = serviceEntries(row).length;
            const isExpanded = !!expanded[row.id] && total > 0;
            const ExpandIcon = isExpanded ? MdExpandLess : MdExpandMore;

            return (
              <div key={row.id}>
                <div className="py-2 pl-[12px] pr-1">
                  <div className="flex items-center gap-2.5">
                    <CarrierLogo src={carrierLogo(row)} name={name} size="sm" dimmed={!on} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold leading-snug text-ui-text">
                        {name}
                      </div>
                      <div className="flex min-w-0 items-center gap-1 text-xs leading-snug text-ui-text-muted">
                        <span className="truncate">{row.account_name ?? "-"}</span>
                        <span aria-hidden="true">·</span>
                        {total > 0 ? (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(row.id)}
                            aria-expanded={isExpanded}
                            className="flex shrink-0 items-center gap-0.5 font-medium text-brand"
                          >
                            {t("carriers.servicesOnShort", {
                              on: servicesOnCount(row),
                              total,
                            })}
                            <ExpandIcon className="h-4 w-4" />
                          </button>
                        ) : (
                          <span className="shrink-0">{t("carriers.noServices")}</span>
                        )}
                      </div>
                    </div>
                    <CarrierToggle
                      checked={on}
                      onChange={(next) => setEnabled([row], next)}
                      disabled={busy[row.id]}
                      label={toggleLabel(row)}
                    />
                  </div>
                  {/* Indented to line up with the text, past the 56px logo + gap. */}
                  <div className="mt-1 flex gap-1.5 pl-[66px]">
                    <AccountActions {...actionsFor(row)} buttonClassName="h-7" />
                  </div>
                </div>
                {isExpanded && (
                  <div className="bg-ui-surface-sunken px-[12px] pb-[12px] pt-2.5">
                    <ServiceChecklist
                      row={row}
                      onChange={(changes) => changeServices(row, changes)}
                      disabled={busy[row.id]}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {view === "list" ? (
          <div className="hidden overflow-x-auto rounded-xl border border-ui-border bg-ui-surface sm:block">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr className="bg-ui-surface-sunken">
                  <th className={`${HEAD_CELL} text-left`}>{t("carriers.columns.carrier")}</th>
                  <th className={`${HEAD_CELL} text-left`}>{t("carriers.columns.accountName")}</th>
                  <th className={`${HEAD_CELL} text-left`}>{t("carriers.columns.services")}</th>
                  <th className={`${HEAD_CELL} text-left`}>{t("carriers.columns.inQuotes")}</th>
                  <th className={`${HEAD_CELL} text-right`}>{t("carriers.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const name = carrierName(row);
                  const on = isCarrierOn(row);
                  const isExpanded = !!expanded[row.id] && serviceEntries(row).length > 0;

                  return (
                    <React.Fragment key={row.id}>
                      <tr className="border-t border-ui-border-subtle hover:bg-ui-row-hover">
                        <td className="px-3.5 py-2">
                          <div className="flex items-center gap-2.5">
                            <CarrierLogo src={carrierLogo(row)} name={name} size="sm" dimmed={!on} />
                            <span className="whitespace-nowrap text-[13px] font-semibold text-ui-text">
                              {name}
                            </span>
                          </div>
                        </td>
                        <td className="px-3.5 py-2 text-[13px] text-ui-text-secondary">
                          {row.account_name ?? "-"}
                        </td>
                        <td className="px-3.5 py-2">
                          <ServicesPill
                            row={row}
                            expanded={isExpanded}
                            onToggle={() => toggleExpanded(row.id)}
                          />
                        </td>
                        <td className="px-2">
                          <CarrierToggle
                            checked={on}
                            onChange={(next) => setEnabled([row], next)}
                            disabled={busy[row.id]}
                            label={toggleLabel(row)}
                          />
                        </td>
                        <td className="px-3.5 py-2">
                          <div className="flex justify-end gap-1.5">
                            <AccountActions {...actionsFor(row)} />
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-ui-surface-sunken">
                          <td colSpan={5} className="px-3.5 pb-3.5 pt-1">
                            <ServiceChecklist
                              row={row}
                              onChange={(changes) => changeServices(row, changes)}
                              disabled={busy[row.id]}
                              inline
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="hidden grid-cols-[repeat(auto-fill,minmax(270px,1fr))] items-start gap-3 sm:grid">
            {visible.map((row) => (
              <CarrierCard
                key={row.id}
                row={row}
                expanded={!!expanded[row.id]}
                onExpand={() => toggleExpanded(row.id)}
                busy={!!busy[row.id]}
                onToggle={(next) => setEnabled([row], next)}
                onServicesChange={(changes) => changeServices(row, changes)}
                subtitle={row.account_name ?? "-"}
                toggleLabel={toggleLabel(row)}
                actions={<AccountActions {...actionsFor(row)} buttonClassName="h-7" />}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <SearchToolbar value={search} onChange={setSearch} placeholder={t("carriers.searchAccounts")}>
        <ConnectButton onClick={() => setConnectOpen(true)} className="ml-auto h-[34px] shrink-0" />
      </SearchToolbar>

      <section className="flex flex-col gap-3">
        <SectionHeader
          title={t("carriers.connectedAccounts")}
          count={loading ? "–" : visible.length}
          hint={t("carriers.connectedHint")}
        >
          <ExpandAllButton
            expanded={allExpanded}
            onClick={toggleAllExpanded}
            disabled={!visible.length}
          />
          <ViewSwitch value={view} onChange={setView} ariaLabel={t("carriers.accountView")} />
        </SectionHeader>
        {renderBody()}
      </section>

      {editing && (
        <EditAccountDialog row={editing} onSave={rename} onClose={() => setEditing(null)} />
      )}
      {disconnecting && (
        <DisconnectDialog
          row={disconnecting}
          onConfirm={remove}
          onClose={() => setDisconnecting(null)}
        />
      )}
      {connectOpen && (
        <ConnectAccountDialog
          upcoming={upcoming}
          onClose={() => setConnectOpen(false)}
          onConnected={reload}
        />
      )}
    </>
  );
};

export default AccountsTab;
